import React, { useState } from 'react'
import { useParams } from 'react-router'
import { MajorAccountChange, RemovePersonTarget } from '_gql'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import AddCompanyOwnerDrawer from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/AddCompanyOwnerDrawer'
import { useGetPeopleInfoData } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/GetPeopleInfoData.gql'
import PersonRecordWithActions from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/PersonRecordWithActions'
import PersonSection from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/PersonSection'
import { useRemovePersonFromRole } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/RemovePersonFromRole.gql'
import UpdateCompanyOwnerDrawer from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/UpdateCompanyOwnerDrawer'
import { person } from '@sputnik/schema/creditApplication/person'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import AddAuthorizedSignerBanner from './AddAuthorizedSignerBanner'

const CompanyOwnerSection = () => {
  const { appId } = useParams()
  const showToast = useToast()
  const { hasMajorAccountChange, isMajorAccountChange } = useGetCreditAppTypes({ salesforceCreditId: appId ?? '' })
  const { track } = useWebTracking()

  const [isAddingCompanyOwner, setIsAddingCompanyOwner] = useState(false)
  const [isShowingBanner, setIsShowingBanner] = useState(true)

  const [removePersonFromRole] = useRemovePersonFromRole({
    onCompleted: () => {
      logInfo('removeCompanyOwner', {
        attributes: {
          creditAppId: appId,
          result: `company owner removed successfully`,
        },
        eventName: 'removeCompanyOwner',
      })
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Removed company owner'
          id='sputnik.CompanyOwnerSection__C3vXpm'
        />,
        {
          autoHideDelay: 5000,
        },
      )
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          creditAppId: appId,
          action: 'removeCompanyOwner',
          message: error?.message,
          result: 'could not remove company owner',
        },
        eventName: 'removeCompanyOwner',
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to remove company owner'
          id='sputnik.CompanyOwnerSection__CSUP3B'
        />,
        { className: 'dd-privacy-allow' },
      )
    },
  })

  const {
    data,
    error,
    loading: isLoadingCompanyOwnersData,
  } = useGetPeopleInfoData({
    variables: {
      creditApplicationId: appId ?? '',
    },
    skip: !appId,
  })

  useApolloQueryErrorHandling({
    error,
    logging: {
      message: 'could not get company owners data',
      eventName: 'GetCompanyOwners',
      attributes: {
        creditAppId: appId,
      },
    },
    toast: {
      message: (
        <FormattedMessage
          defaultMessage='Failed to load company owners data'
          id='sputnik.CompanyOwnerSection__M/LJvo'
        />
      ),
    },
  })

  const onDelete = async (personId: string) => {
    await removePersonFromRole({
      variables: {
        creditApplicationId: appId ?? '',
        personId,
        target: RemovePersonTarget.BENEFICIAL_OWNER,
      },
    })
  }

  const beneficialOwners = data?.creditApplication?.beneficialOwners || []
  const authorizedSigner = data?.creditApplication?.authorizedSigner
  const hasBeneficialOwners = beneficialOwners.length > 0

  const hasAddedAuthSignerAsCompanyOwner = beneficialOwners.some(
    (owner) =>
      (authorizedSigner && owner?.email === authorizedSigner?.email) ||
      (authorizedSigner && owner?.id === authorizedSigner?.id),
  )

  const hasBeneficialOwnerMajorAccountChange = hasMajorAccountChange(
    MajorAccountChange.AUTHORIZED_SIGNER,
    MajorAccountChange.BENEFICIAL_OWNER_0,
    MajorAccountChange.BENEFICIAL_OWNER_1,
    MajorAccountChange.BENEFICIAL_OWNER_2,
    MajorAccountChange.BENEFICIAL_OWNER_3,
  )

  // We want to disable the add person button to be disabled when
  // * company owner data is loading
  // * there exists 4 company owners (this is the max allowed)
  // * it is a MAC and there was no requested changes to beneficial owners
  const shouldDisableAddPersonButton =
    isLoadingCompanyOwnersData ||
    beneficialOwners.length === 4 ||
    (isMajorAccountChange && !hasBeneficialOwnerMajorAccountChange)

  return (
    <PersonSection
      disableAddPersonButton={shouldDisableAddPersonButton}
      subtitle={
        <FormattedMessage
          defaultMessage='A company owner is someone who owns at least 25% of the company'
          id='sputnik.CompanyOwnerSection__6D+soQ'
        />
      }
      title={
        <FormattedMessage
          defaultMessage='Company owners'
          id='sputnik.CompanyOwnerSection__2DRSsN'
        />
      }
      onOpenDrawer={() => {
        track('se_signup__add_person__fired', {
          person_type: 'companyOwner',
        })
        setIsAddingCompanyOwner(true)
      }}
    >
      {isShowingBanner && !hasAddedAuthSignerAsCompanyOwner && authorizedSigner && (
        <AddAuthorizedSignerBanner
          authorizedSigner={authorizedSigner}
          onClick={() => setIsShowingBanner(false)}
        />
      )}
      {hasBeneficialOwners &&
        beneficialOwners?.map((owner) => {
          const isAuthorizedSigner =
            !!authorizedSigner && (owner?.email === authorizedSigner?.email || owner?.id === authorizedSigner?.id)

          const { passportCountry, passportExpiration, passportNumberLastFour, address } = owner ?? {}

          const infoComplete = person.isValidSync({
            ...owner,
            ...owner?.address,
            isForeignAddress: address?.countryCode && address?.countryCode !== 'US' ? 'YES' : 'NO',
            isNonUSCitizen: !!passportCountry || !!passportExpiration || !!passportNumberLastFour,
          })

          // We want to disable the actions when one of two situations occurs during a MAC
          // * There is not a befeficial owner change requested
          // * There is a beneficial owner change and the following
          //   * the beneficial owner is the authorized signer
          //   * there is not a requst for the authorized signer to be changed
          const shouldDisableEditButton =
            isMajorAccountChange &&
            (!hasBeneficialOwnerMajorAccountChange ||
              (hasBeneficialOwnerMajorAccountChange &&
                isAuthorizedSigner &&
                !hasMajorAccountChange(MajorAccountChange.AUTHORIZED_SIGNER)))
          return (
            <PersonRecordWithActions
              key={owner?.id}
              disableDelete={isMajorAccountChange && !hasBeneficialOwnerMajorAccountChange}
              disableEdit={shouldDisableEditButton}
              editDrawer={UpdateCompanyOwnerDrawer}
              firstName={owner?.firstName || ''}
              lastName={owner?.lastName || ''}
              personId={owner?.id || ''}
              subtext={{
                showTag: !!isAuthorizedSigner,
                infoComplete: infoComplete,
              }}
              trackerId='companyOwner'
              onDeleteClick={onDelete}
            />
          )
        })}
      <AddCompanyOwnerDrawer
        isShowing={isAddingCompanyOwner}
        onClose={() => {
          track('se_signup__add_person_drawer_cancelled__fired', {
            person_type: 'companyOwner',
          })
          setIsAddingCompanyOwner(false)
        }}
      />
    </PersonSection>
  )
}

export default CompanyOwnerSection
